import { useEffect, useMemo, useState } from "react";
import {
  ChevronRight, Download, Lock, Plus, Save, Sparkles, Trash2, User, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  addFolio,
  updateFolio,
  deleteFolio,
  addTransaction,
  updateTransaction,
  deleteTransaction,
  downloadFolioPdf,
  downloadFolioDocx,
  getCompany,
  updateCompany,
} from "@/lib/api";
import { EntryForm, formatCell } from "./EntryForm";

// Map folio header keys → company.member field names. Used when persisting
// edits made to auto-derived folios for the MGT-1 (members) register.
const MEMBER_FOLIO_HEADER_TO_MEMBER_FIELD = {
  name_of_member: "name",
  address: "address",
  total_shares_held: "shares_held",
  class_of_shares: "member_type",
  nominal_value_per_share: "face_value",
  folio_no: "folio_no",
  status: "status",
  father_mother_spouse: "father_name",
  pan_no: "pan_no",
  unique_id_no: "unique_id_no",
  occupation: "occupation",
  email: "email",
  nationality: "nationality",
};

// Map folio header keys → company.director field names. Used for MBP-2 Part I.
const DIRECTOR_FOLIO_HEADER_TO_DIRECTOR_FIELD = {
  din: "din",
  present_name: "name",
  father_name: "father_name",
  date_of_birth: "dob",
  nationality: "nationality",
  occupation: "occupation",
  present_address: "address",
  date_of_appointment: "appointment_date",
  pan_no: "pan",
};

/**
 * Folio-mode register UI (MGT-1 / MBP-2 Part I / MGT-2).
 *
 * Master-detail: list of folios on the left → click into one → see header
 * details + transactions sub-table on the right.
 */
export function FolioRegister({ companyId, registerKey, data, activeMeta, onReload, onDownload, members = [] }) {
  const headerFields = data.register.header_fields || [];
  const txnCols = data.register.transaction_columns || [];
  const folios = useMemo(() => data.folios || [], [data.folios]);
  const [selectedId, setSelectedId] = useState(folios[0]?.id || null);
  const [addingFolio, setAddingFolio] = useState(false);
  const [folioDraft, setFolioDraft] = useState({});
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({});
  const [addingTxn, setAddingTxn] = useState(false);
  const [txnDraft, setTxnDraft] = useState({});
  const [editingTxnId, setEditingTxnId] = useState(null);
  const [txnEditDraft, setTxnEditDraft] = useState({});

  // Keep selection valid when the folio list reloads.
  useEffect(() => {
    if (folios.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!folios.some((f) => f.id === selectedId)) {
      setSelectedId(folios[0].id);
    }
  }, [folios, selectedId]);

  const selected = folios.find((f) => f.id === selectedId) || null;

  const folioLabel = (f) => {
    const h = f.header || {};
    return h.name_of_member || h.present_name || h.name_of_holder || "(unnamed)";
  };

  const folioSubtitle = (f) => {
    const h = f.header || {};
    if (registerKey === "members") return `Folio · ${h.total_shares_held || 0} ${h.class_of_shares || "shares"}`;
    if (registerKey === "directors_kmp") return `DIN: ${h.din || "—"} · ${h.occupation || "Director"}`;
    if (registerKey === "debenture_holders") return `${h.total_held || 0} × ${h.class_of_securities || "debentures"}`;
    return "";
  };

  const handleCreateFolio = async () => {
    try {
      const res = await addFolio(companyId, registerKey, folioDraft);
      toast.success("Folio created");
      setAddingFolio(false);
      setFolioDraft({});
      await onReload();
      setSelectedId(res.data?.folio?.id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create folio");
    }
  };

  const handleSaveHeader = async () => {
    try {
      if (selected._auto) {
        // Auto folio — there's no DB row. Write the header edits back to the
        // underlying company.members / company.directors array.
        await persistAutoFolioEdit(selected, headerDraft, registerKey, companyId);
      } else {
        await updateFolio(companyId, registerKey, selected.id, headerDraft);
      }
      toast.success("Folio updated");
      setEditingHeader(false);
      setHeaderDraft({});
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update folio");
    }
  };

  const handleDeleteFolio = async () => {
    if (!selected || selected._auto) return;
    if (!window.confirm("Delete this folio and all its transactions?")) return;
    try {
      await deleteFolio(companyId, registerKey, selected.id);
      toast.success("Folio deleted");
      setSelectedId(null);
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const handleAddTxn = async () => {
    try {
      await addTransaction(companyId, registerKey, selected.id, txnDraft);
      toast.success("Transaction added");
      setAddingTxn(false);
      setTxnDraft({});
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add transaction");
    }
  };

  const handleUpdateTxn = async (txnId) => {
    try {
      await updateTransaction(companyId, registerKey, selected.id, txnId, txnEditDraft);
      toast.success("Transaction updated");
      setEditingTxnId(null);
      setTxnEditDraft({});
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const handleDeleteTxn = async (txnId) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await deleteTransaction(companyId, registerKey, selected.id, txnId);
      toast.success("Transaction deleted");
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const handleDownloadFolio = async () => {
    if (!selected) return;
    try {
      await downloadFolioPdf(
        companyId, registerKey, selected.id,
        `${data.register.form_number.replace(/\s/g, "_")}_${folioLabel(selected).replace(/[^A-Za-z0-9_-]/g, "_").slice(0, 50)}`,
      );
      toast.success("Folio PDF downloaded");
    } catch {
      toast.error("Could not download folio PDF");
    }
  };

  const handleDownloadFolioDocx = async () => {
    if (!selected) return;
    try {
      await downloadFolioDocx(
        companyId, registerKey, selected.id,
        `${data.register.form_number.replace(/\s/g, "_")}_${folioLabel(selected).replace(/[^A-Za-z0-9_-]/g, "_").slice(0, 50)}`,
      );
      toast.success("Folio Word document downloaded");
    } catch {
      toast.error("Could not download folio DOCX");
    }
  };

  return (
    <div className="space-y-4" data-testid={`folio-register-${registerKey}`}>
      <FolioHeader
        registerKey={registerKey}
        data={data}
        activeMeta={activeMeta}
        onDownload={onDownload}
        onAdd={() => { setAddingFolio(true); setFolioDraft({}); }}
      />

      {addingFolio && (
        <NewFolioForm
          headerFields={headerFields}
          draft={folioDraft}
          onChange={setFolioDraft}
          onCancel={() => setAddingFolio(false)}
          onCreate={handleCreateFolio}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        <FolioList
          folios={folios}
          selectedId={selectedId}
          onSelect={setSelectedId}
          folioLabel={folioLabel}
          folioSubtitle={folioSubtitle}
        />

        <section className="space-y-4 min-w-0" data-testid="folio-detail">
          {!selected ? (
            <div className="border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              Select a folio on the left, or create a new one.
            </div>
          ) : (
            <>
              <FolioHeaderCard
                selected={selected}
                headerFields={headerFields}
                folioLabel={folioLabel}
                editingHeader={editingHeader}
                headerDraft={headerDraft}
                onHeaderDraftChange={setHeaderDraft}
                onEdit={() => { setEditingHeader(true); setHeaderDraft(selected.header || {}); }}
                onCancelEdit={() => { setEditingHeader(false); setHeaderDraft({}); }}
                onSave={handleSaveHeader}
                onDelete={handleDeleteFolio}
                onDownload={handleDownloadFolio}
                onDownloadDocx={handleDownloadFolioDocx}
              />

              <TransactionsTable
                txnCols={txnCols}
                transactions={selected.transactions || []}
                members={members}
                addingTxn={addingTxn}
                txnDraft={txnDraft}
                onTxnDraftChange={setTxnDraft}
                onAddOpen={() => { setAddingTxn(true); setTxnDraft({}); }}
                onAddCancel={() => setAddingTxn(false)}
                onAdd={handleAddTxn}
                editingTxnId={editingTxnId}
                txnEditDraft={txnEditDraft}
                onTxnEditDraftChange={setTxnEditDraft}
                onEditOpen={(t) => { setEditingTxnId(t.id); setTxnEditDraft(t); }}
                onEditCancel={() => { setEditingTxnId(null); setTxnEditDraft({}); }}
                onUpdate={handleUpdateTxn}
                onDelete={handleDeleteTxn}
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function FolioHeader({ registerKey, data, activeMeta, onDownload, onAdd }) {
  return (
    <div className="border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
            {data.register.form_number}
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mt-0.5">{data.register.label}</h2>
          <p className="text-xs text-slate-500 italic mt-1">{data.register.section}</p>
          <p className="text-[11px] text-slate-500 mt-2">
            Per-holder folio format: each member / director / holder has their own page with personal
            details + a transaction history sub-table.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => onDownload("pdf")}
            className="px-3 py-1.5 border border-slate-300 text-xs hover:border-blue-700 hover:text-blue-700 flex items-center gap-1.5"
            data-testid={`register-${registerKey}-download-pdf`}
          >
            <Download className="w-3 h-3" /> All Folios PDF
          </button>
          <button
            onClick={() => onDownload("docx")}
            className="px-3 py-1.5 border border-slate-300 text-xs hover:border-blue-700 hover:text-blue-700 flex items-center gap-1.5"
            data-testid={`register-${registerKey}-download-docx`}
          >
            <Download className="w-3 h-3" /> All Folios WORD
          </button>
          <button
            onClick={onAdd}
            className="px-3 py-1.5 bg-blue-700 text-white text-xs flex items-center gap-1.5 hover:bg-blue-800"
            data-testid={`register-${registerKey}-add-folio`}
          >
            <Plus className="w-3 h-3" /> New Folio
          </button>
        </div>
      </div>
      {activeMeta && (
        <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
          <span>Total Folios: <strong className="text-slate-800">{activeMeta.total_count}</strong></span>
          {activeMeta.auto_count > 0 && (
            <span>Auto-derived: <strong className="text-blue-700">{activeMeta.auto_count}</strong></span>
          )}
          <span>Manual: <strong className="text-slate-800">{activeMeta.manual_count}</strong></span>
        </div>
      )}
    </div>
  );
}

function NewFolioForm({ headerFields, draft, onChange, onCancel, onCreate }) {
  return (
    <div className="border border-blue-200 bg-blue-50/40 p-5" data-testid="folio-add-form">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">New Folio · Holder Details</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-900">
          <X className="w-4 h-4" />
        </button>
      </div>
      <EntryForm columns={headerFields} value={draft} onChange={onChange} />
      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={onCreate}
          className="px-3 py-1.5 bg-blue-700 text-white text-xs flex items-center gap-1.5 hover:bg-blue-800"
          data-testid="folio-create"
        >
          <Save className="w-3 h-3" /> Create Folio
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 border border-slate-300 text-xs hover:bg-slate-50">
          Cancel
        </button>
      </div>
    </div>
  );
}

function FolioList({ folios, selectedId, onSelect, folioLabel, folioSubtitle }) {
  return (
    <aside className="border border-slate-200 bg-white" data-testid="folio-list">
      {folios.length === 0 ? (
        <div className="p-5 text-center text-xs text-slate-500">
          No folios yet. Click <strong>New Folio</strong> to start.
        </div>
      ) : (
        <ul>
          {folios.map((f) => {
            const isSel = f.id === selectedId;
            const isAuto = f._auto;
            return (
              <li key={f.id}>
                <button
                  onClick={() => onSelect(f.id)}
                  className={`w-full px-4 py-3 text-left border-b border-slate-100 transition flex items-start gap-2 ${
                    isSel ? "bg-blue-50 border-l-2 border-l-blue-700" : "hover:bg-slate-50"
                  }`}
                  data-testid={`folio-pick-${f.id}`}
                >
                  <User className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate" title={folioLabel(f)}>
                      {folioLabel(f)}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{folioSubtitle(f)}</div>
                    {isAuto && (
                      <div className="text-[10px] text-blue-700 mt-0.5 flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> Auto · {f._source}
                      </div>
                    )}
                  </div>
                  <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${isSel ? "text-blue-700" : "text-slate-300"}`} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

function FolioHeaderCard({
  selected, headerFields, folioLabel, editingHeader, headerDraft, onHeaderDraftChange,
  onEdit, onCancelEdit, onSave, onDelete, onDownload, onDownloadDocx,
}) {
  return (
    <div className="border border-slate-200 bg-white p-5">
      {/* Sticky action bar — stays visible above the transactions table even
          after the user scrolls past long header / large transactions list.
          Fixes "Download button gets hidden after adding a transaction". */}
      <div className="sticky top-0 -mt-5 -mx-5 mb-4 px-5 py-3 bg-white border-b border-slate-100 z-10 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Folio</div>
          <h3 className="text-lg font-semibold text-slate-900">{folioLabel(selected)}</h3>
          {selected._auto && (
            <div className="text-[11px] text-blue-700 mt-1 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Auto-derived from {selected._source} — edits save back to the Company record.
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={onDownload}
            className="px-2.5 py-1 border border-slate-300 text-[11px] hover:border-blue-700 hover:text-blue-700 flex items-center gap-1 bg-white"
            data-testid="folio-pdf"
          >
            <Download className="w-3 h-3" /> PDF
          </button>
          {onDownloadDocx && (
            <button
              onClick={onDownloadDocx}
              className="px-2.5 py-1 border border-slate-300 text-[11px] hover:border-blue-700 hover:text-blue-700 flex items-center gap-1 bg-white"
              data-testid="folio-docx"
            >
              <Download className="w-3 h-3" /> Word
            </button>
          )}
          {!editingHeader && (
            <button
              onClick={onEdit}
              className="px-2.5 py-1 border border-slate-300 text-[11px] hover:border-blue-700 hover:text-blue-700 bg-white"
              data-testid="folio-edit-header"
            >
              Edit
            </button>
          )}
          {!selected._auto && (
            <button
              onClick={onDelete}
              className="p-1 text-slate-400 hover:text-red-600"
              data-testid="folio-delete"
              title="Delete folio"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {editingHeader ? (
        <>
          <EntryForm columns={headerFields} value={headerDraft} onChange={onHeaderDraftChange} />
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onSave}
              className="px-3 py-1.5 bg-blue-700 text-white text-xs flex items-center gap-1.5 hover:bg-blue-800"
              data-testid="folio-save-header"
            >
              <Save className="w-3 h-3" /> Save
            </button>
            <button onClick={onCancelEdit} className="px-3 py-1.5 border border-slate-300 text-xs hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {headerFields.map((c) => {
            const v = selected.header?.[c.key];
            if (v === undefined || v === null || v === "" || v === 0) return null;
            return (
              <div key={c.key} className="border-b border-slate-100 pb-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</div>
                <div className="text-sm text-slate-900 mt-0.5 break-words">{formatCell(c.type, v)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TransactionsTable({
  txnCols, transactions, members = [],
  addingTxn, txnDraft, onTxnDraftChange, onAddOpen, onAddCancel, onAdd,
  editingTxnId, txnEditDraft, onTxnEditDraftChange, onEditOpen, onEditCancel, onUpdate, onDelete,
}) {
  const COMPACT_COUNT = 3;
  const [expanded, setExpanded] = useState(false);
  // When the user adds / edits a transaction, auto-expand so they can see it.
  const showAll = expanded || addingTxn || editingTxnId != null || transactions.length <= COMPACT_COUNT;
  const visibleTransactions = showAll
    ? transactions
    : transactions.slice(Math.max(0, transactions.length - COMPACT_COUNT));
  const hiddenCount = transactions.length - visibleTransactions.length;

  return (
    <div className="border border-slate-200 bg-white" data-testid="folio-transactions">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">
            Transaction History
            {transactions.length > 0 && (
              <span className="text-[11px] text-slate-500 font-normal ml-2">
                ({transactions.length} total)
              </span>
            )}
          </h4>
          <p className="text-[11px] text-slate-500">
            Allotments / transfers / acquisitions / disposals / forfeitures for this folio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {transactions.length > COMPACT_COUNT && !addingTxn && editingTxnId == null && (
            <button
              onClick={() => setExpanded((s) => !s)}
              className="px-2.5 py-1 border border-slate-300 text-[11px] hover:border-blue-700 hover:text-blue-700"
              data-testid="folio-toggle-compact"
            >
              {expanded ? "Show only latest 3" : `Show all ${transactions.length}`}
            </button>
          )}
          <button
            onClick={onAddOpen}
            className="px-3 py-1.5 bg-blue-700 text-white text-xs flex items-center gap-1.5 hover:bg-blue-800"
            data-testid="folio-add-txn"
          >
            <Plus className="w-3 h-3" /> Add Transaction
          </button>
        </div>
      </div>

      {hiddenCount > 0 && !expanded && (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 text-[11px] text-amber-800" data-testid="folio-hidden-banner">
          Showing the latest {visibleTransactions.length} of {transactions.length} transactions — {hiddenCount} earlier transactions hidden. Click "Show all" to see the full history.
        </div>
      )}

      {addingTxn && (
        <div className="bg-blue-50/40 p-5 border-b border-blue-100" data-testid="folio-txn-form">
          <EntryForm columns={txnCols} value={txnDraft} onChange={onTxnDraftChange} members={members} />
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={onAdd}
              className="px-3 py-1.5 bg-blue-700 text-white text-xs flex items-center gap-1.5 hover:bg-blue-800"
              data-testid="folio-txn-save"
            >
              <Save className="w-3 h-3" /> Save
            </button>
            <button onClick={onAddCancel} className="px-3 py-1.5 border border-slate-300 text-xs hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No transactions on this folio yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="folio-txn-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left w-12">#</th>
                {txnCols.map((c) => (
                  <th key={c.key} className="px-3 py-2 text-left whitespace-nowrap">{c.label}</th>
                ))}
                <th className="px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {visibleTransactions.map((t, vIdx) => {
                // `displayIdx` keeps the # column aligned with the actual
                // position in the full history (so users see 4, 5, 6 in
                // compact mode instead of 1, 2, 3).
                const idx = showAll ? vIdx : (transactions.length - visibleTransactions.length + vIdx);
                const isEditing = editingTxnId === t.id;
                return (
                  <tr key={t.id || idx} className="border-t border-slate-100" data-testid={`folio-txn-row-${idx}`}>
                    <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                    {isEditing ? (
                      <td colSpan={txnCols.length}>
                        <div className="p-3">
                          <EntryForm columns={txnCols} value={txnEditDraft} onChange={onTxnEditDraftChange} members={members} />
                          <div className="mt-2 flex gap-2">
                            <button onClick={() => onUpdate(t.id)} className="px-3 py-1 bg-blue-700 text-white text-xs">Save</button>
                            <button onClick={onEditCancel} className="px-3 py-1 border text-xs">Cancel</button>
                          </div>
                        </div>
                      </td>
                    ) : (
                      txnCols.map((c) => (
                        <td key={c.key} className="px-3 py-2 align-top max-w-xs">
                          <div className="truncate" title={String(t[c.key] ?? "")}>
                            {formatCell(c.type, t[c.key])}
                          </div>
                        </td>
                      ))
                    )}
                    {!isEditing && (
                      <td className="px-3 py-2 align-top text-right">
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => onEditOpen(t)}
                            className="text-[10px] text-slate-500 hover:text-blue-700 px-1.5 py-0.5"
                            data-testid={`folio-txn-edit-${idx}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => onDelete(t.id)}
                            className="text-slate-400 hover:text-red-600 p-1"
                            data-testid={`folio-txn-delete-${idx}`}
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Persist auto-folio edits back to company.members / company.directors.
// `folio._member_index` (or _source) tells us which underlying record to patch.
async function persistAutoFolioEdit(folio, headerDraft, registerKey, companyId) {
  const cur = (await getCompany(companyId)).data;

  if (registerKey === "members") {
    const arr = [...(cur.members || [])];
    const idx = typeof folio._member_index === "number"
      ? folio._member_index
      : arr.findIndex((m) => m.name === folio.header?.name_of_member);
    if (idx < 0 || idx >= arr.length) throw new Error("Could not locate underlying member record");
    const updated = { ...arr[idx] };
    for (const [hk, mk] of Object.entries(MEMBER_FOLIO_HEADER_TO_MEMBER_FIELD)) {
      if (Object.prototype.hasOwnProperty.call(headerDraft, hk)) {
        updated[mk] = headerDraft[hk];
      }
    }
    arr[idx] = updated;
    await updateCompany(companyId, { members: arr });
    return;
  }

  if (registerKey === "directors_kmp") {
    const arr = [...(cur.directors || [])];
    const din = folio.header?.din || folio._din;
    let idx = din ? arr.findIndex((d) => d.din && d.din === din) : -1;
    if (idx < 0) idx = arr.findIndex((d) => d.name === folio.header?.present_name);
    if (idx < 0 || idx >= arr.length) throw new Error("Could not locate underlying director record");
    const updated = { ...arr[idx] };
    for (const [hk, dk] of Object.entries(DIRECTOR_FOLIO_HEADER_TO_DIRECTOR_FIELD)) {
      if (Object.prototype.hasOwnProperty.call(headerDraft, hk)) {
        updated[dk] = headerDraft[hk];
      }
    }
    arr[idx] = updated;
    await updateCompany(companyId, { directors: arr });
    return;
  }

  throw new Error(`Auto folio edits not supported for register '${registerKey}'`);
}

